/**
 * The ad slot: one of the banners from `ads/`, drawn from this session's
 * shuffled queue.
 *
 * Deliberately plain: a hairline border, a rounded corner, and nothing else.
 * These are fake ads and the joke only works if the frame around them looks
 * like a real ad slot rather than like a feature of the app.
 *
 * **Drawn on mount, never on re-render.** `useState` with an initialiser rather
 * than a bare call in the body: React re-renders a component whenever a parent's
 * state changes, and an ad that changes while somebody is looking at it is a
 * flicker, not a rotation. See `adQueue.ts` for the order.
 *
 * Drawing inside the initialiser is impure, and StrictMode invokes it twice in
 * development, so a dev mount burns two entries instead of one. That is the
 * accepted cost. The alternative is drawing in an effect, which means the first
 * paint has no ad and therefore no height for it, and a guaranteed layout shift
 * on every page load in production is a real defect where a dev-only double
 * draw from a seventeen-long queue is not.
 *
 * `width`/`height` come from the generated manifest so the box is the right
 * size before the image arrives. On /join especially, where the slot is on
 * screen at load, the alternative is the Continue button jumping.
 *
 * Tapping it rolls another one, which is the only interaction a fake ad should
 * have: it never navigates anywhere, because there is nowhere for it to go.
 *
 * If an image 404s the whole slot removes itself. A broken-image icon at the
 * bottom of the login screen reads as a broken app.
 */

import { useCallback, useState } from 'react'
import { nextAd } from './adQueue'
import { type Ad } from './ads'

export function AdSlot({ marginTop = 28 }: { marginTop?: number }) {
  const [ad, setAd] = useState<Ad | undefined>(() => nextAd())
  const [broken, setBroken] = useState(false)

  // A tap draws from the same queue, so it advances the rotation rather than
  // sitting outside it. Tapping through the whole set and tapping through it by
  // refreshing reach the same seventeen ads.
  const reroll = useCallback(() => {
    setBroken(false)
    setAd((current) => nextAd() ?? current)
  }, [])

  if (!ad || broken) return null

  return (
    <div
      style={{
        marginTop,
        width: '100%',
        maxWidth: ad.width,
        marginLeft: 'auto',
        marginRight: 'auto',
      }}
    >
      <button
        onClick={reroll}
        aria-label="Show another ad"
        style={{ display: 'block', width: '100%', padding: 0, background: 'none', border: 'none' }}
      >
        <img
          src={ad.src}
          alt=""
          width={ad.width}
          height={ad.height}
          loading="lazy"
          decoding="async"
          onError={() => setBroken(true)}
          style={{
            display: 'block',
            width: '100%',
            height: 'auto',
            aspectRatio: `${ad.width} / ${ad.height}`,
            borderRadius: 12,
            border: '1px solid var(--line)',
          }}
        />
      </button>
    </div>
  )
}
